import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, LayoutPanelLeft } from 'lucide-react';
// Wizard renders inside SidebarLayout; no custom brand/logo in the topbar.
import type { Data } from '@puckeditor/core';

import { Button } from '@/components/ui/button';
import { FloatingInput } from '@/components/ui/floating-input';
import { FaviconField } from '@/components/sites/FaviconField';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { EmptyState } from '@/components/ui/empty-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingState } from '@/components/ui/loading-state';
import { useDelayedLoader } from '@/hooks/useDelayedLoader';

import { SiteEditor } from '../features/editor/SiteEditor';
import { useIndexedDbDraft } from '../features/editor/useIndexedDbDraft';
import { useSubdomainCheck } from '../hooks/useSites';
import { APP_CONFIG } from '../config/app.config';
import { fetchTemplateBundle, createSiteFromBundle } from '../features/editor/api';
import { TemplatePreviewPane } from '../components/sites/TemplatePreviewPane';
import styles from './NewSite.module.css';

type WizardStep = 'customize' | 'launch';

/**
 * Two-step wizard for customizing a template and creating a new draft site.
 * A `template_id` query param is required — the template picker lives on
 * `/templates`. If the route is hit without a template, we show an empty
 * state that links to `/templates`.
 *
 * The site does not exist server-side until "Create site" is clicked;
 * the user edits locally with an IndexedDB safety net keyed by
 * `pending:<templateId>`. The wizard ends at the `draft` site row and
 * redirects to `/sites/:id` — publishing (free or paid) is initiated from
 * the site detail page, never from this flow.
 *
 * This route renders outside the app's SidebarLayout so the customize
 * step can use the full viewport for the Puck editor.
 */
export const NewSite = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const templateId = params.get('template_id');

  const [currentStep, setCurrentStep] = useState<WizardStep>('customize');

  const [data, setData] = useState<Data | null>(null);
  const [themeCss, setThemeCss] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [meta, setMeta] = useState<Record<string, unknown>>({});

  const [title, setTitle] = useState('');
  const [favicon, setFavicon] = useState('');
  const [subdomain, setSubdomain] = useState('');

  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ title?: string; subdomain?: string }>({});

  // Live availability check for the subdomain field.
  const {
    checking: subdomainChecking,
    result: subdomainResult,
    checkSubdomain,
  } = useSubdomainCheck();

  const dbKey = `pending:${templateId ?? ''}`;
  const idb = useIndexedDbDraft(dbKey);

  // Load the template bundle when template_id changes. We deliberately do NOT
  // read the IndexedDB draft here — the wizard is for *new* sites and nothing
  // has been committed server-side, so auto-applying a prior draft tends to
  // surface stale experimentation as "the template's defaults". The autosave
  // below still persists the current edit session as a snapshot for potential
  // future manual recovery UX, but never auto-rehydrates from it.
  useEffect(() => {
    if (!templateId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const bundle = await fetchTemplateBundle(templateId);
        if (cancelled) return;
        setThemeCss(bundle.themeCss ?? '');
        setCustomCss(bundle.customCss ?? '');
        setMeta((bundle.meta ?? {}) as Record<string, unknown>);
        // Puck accepts only { root, content, zones }. The backend adds extras
        // like `$blocksVersion`; keeping them makes Puck silently render an
        // empty canvas. Strip to the known-good shape before handing over.
        //
        // Every content item must also carry a unique `props.id` — Puck's
        // DropZone uses it as a React key and a drag/overlay tracking id.
        // Template JSONs authored by hand often omit it; <Render> is permissive
        // but the <Puck> editor renders blocks at 0 height when ids collide
        // (and emits a "Each child in a list should have a unique key" warning).
        const raw = bundle.data as {
          root?: unknown;
          content?: Array<{ type: string; props?: Record<string, unknown> }>;
          zones?: unknown;
        };
        const ensureId = (items: Array<{ type: string; props?: Record<string, unknown> }>) =>
          items.map((item, i) => ({
            ...item,
            props: {
              ...(item.props ?? {}),
              id:
                (item.props?.id as string | undefined) ??
                `${item.type}-${i}-${Math.random().toString(36).slice(2, 9)}`,
            },
          }));
        setData({
          root: (raw.root as Data['root']) ?? { props: {} },
          content: (ensureId(raw.content ?? []) as unknown) as Data['content'],
          zones: (raw.zones as Data['zones']) ?? {},
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[NewSite] Failed to load template', templateId, err);
        setLoadError(
          "We couldn't load this template right now. Please try again in a moment, or pick a different template.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Safety-net autosave to IndexedDB during customize.
  useEffect(() => {
    if (!data || !idb.ready) return;
    const t = setTimeout(() => {
      void idb.save({ data, themeCss, customCss, meta }, '');
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, themeCss, customCss, meta, idb.ready]);

  const goNext = () => {
    if (currentStep === 'customize' && data) setCurrentStep('launch');
  };

  const exitWizard = () => {
    navigate('/sites');
  };

  // Create a `draft` site row and redirect to its detail page. The publish
  // CTA (free or paid Stripe Checkout) lives on the detail page; this flow
  // is intentionally payment-free.
  const onCreate = useCallback(async () => {
    if (!data || !templateId) return;

    // Validate required fields. Surface inline errors instead of blocking
    // the button — user taps Create, then sees what needs fixing.
    const errors: { title?: string; subdomain?: string } = {};
    if (title.trim().length === 0) errors.title = 'Enter a site title.';
    if (subdomain.trim().length < 3) {
      errors.subdomain = 'Subdomain must be at least 3 characters.';
    } else if (subdomainResult && !subdomainResult.available) {
      errors.subdomain = 'Name is unavailable';
    } else if (subdomainChecking) {
      errors.subdomain = 'Still checking availability — try again in a second.';
    }
    if (errors.title || errors.subdomain) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setCreating(true);
    setLoadError(null);
    try {
      const { site_id } = await createSiteFromBundle(
        templateId,
        subdomain,
        title,
        { data, themeCss, customCss, meta: { ...meta, subdomain, title, favicon } },
      );
      await idb.clear();
      navigate(`/sites/${site_id}`);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Create failed.');
    } finally {
      setCreating(false);
    }
  }, [data, templateId, subdomain, title, favicon, themeCss, customCss, meta, idb, navigate, subdomainChecking, subdomainResult]);

  const showTemplateLoader = useDelayedLoader(loading);

  const canGoNextFromCustomize = !!data && !loading;

  const goBack = () => {
    if (currentStep === 'launch') setCurrentStep('customize');
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="w-full pt-5 flex flex-col flex-1 min-h-0">
        <Breadcrumbs />
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              {currentStep === 'customize' ? 'Customize your template' : 'Name your site'}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              {currentStep === 'customize'
                ? 'Edit text, swap images, and tweak the design. Nothing leaves your browser until you save.'
                : 'Pick a title and subdomain. We\u2019ll save it as a draft \u2014 publish it whenever you\u2019re ready.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {currentStep === 'launch' ? (
              <Button type="button" variant="tertiary" onClick={goBack}>
                <ArrowLeft className="h-4 w-4 stroke-[1.5]" />
                Customize
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={() => navigate('/templates')}
                >
                  <ArrowLeft className="h-4 w-4 stroke-[1.5]" />
                  Select a template
                </Button>
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={!canGoNextFromCustomize}
                >
                  Configure
                  <ArrowRight className="h-4 w-4 stroke-[1.5]" />
                </Button>
              </>
            )}
          </div>
        </div>

        {loadError && (
          <Alert variant="destructive">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}

        <div className={styles.stepContent}>
        {currentStep === 'customize' && !templateId && (
          <EmptyState
            icon={LayoutPanelLeft}
            title="Pick a template to start"
            description="Choose a starting point, then customize it here before publishing."
            action={{ label: 'Browse templates', href: '/templates' }}
          />
        )}

        {currentStep === 'customize' && templateId && (
          <>
            {showTemplateLoader && <LoadingState />}
            {!showTemplateLoader && data && (
              <div className={styles.editorFrame}>
                <SiteEditor
                  key={templateId}
                  mode="new"
                  initialData={data}
                  themeCss={themeCss}
                  customCss={customCss}
                  editorViewport={meta.editorViewport as 'desktop' | 'tablet' | 'mobile' | undefined}
                  onChange={setData}
                />
              </div>
            )}
            {!showTemplateLoader && !data && !loadError && (
              <p className={styles.status}>No template selected.</p>
            )}
          </>
        )}

        {currentStep === 'launch' && (
          <div className={styles.launchSplit}>
            <div className={styles.launchPreview}>
              <TemplatePreviewPane
                templateName={title || 'Live preview'}
                data={data}
                themeCss={themeCss}
                customCss={customCss}
                loading={loading}
                canCustomize={false}
                onCustomize={() => undefined}
                hideHeader
              />
            </div>

            <div className={styles.launchForm}>
              <div className={styles.launchFormBody}>
                <div>
                  <h2 className={styles.launchTitle}>Take it live</h2>
                  <p className={styles.launchSubtitle}>
                    Name your site and pick a subdomain. You can publish it from
                    the site page once it&rsquo;s ready.
                  </p>
                </div>

                <div className={styles.field}>
                  <FloatingInput
                    id="new-site-title"
                    label="Site title"
                    size="md"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (fieldErrors.title) setFieldErrors((prev) => ({ ...prev, title: undefined }));
                    }}
                    autoFocus
                    aria-invalid={!!fieldErrors.title}
                    state={fieldErrors.title ? 'error' : 'default'}
                  />
                  {fieldErrors.title && (
                    <p className="text-sm text-destructive mt-1">{fieldErrors.title}</p>
                  )}
                </div>

                <div className={styles.field}>
                  <FloatingInput
                    id="new-site-subdomain"
                    label="Subdomain"
                    size="md"
                    value={subdomain}
                    onChange={(e) => {
                      const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                      setSubdomain(cleaned);
                      checkSubdomain(cleaned);
                      if (fieldErrors.subdomain) setFieldErrors((prev) => ({ ...prev, subdomain: undefined }));
                    }}
                    aria-invalid={!!fieldErrors.subdomain || (subdomainResult != null && !subdomainResult.available)}
                    suffix={`.${APP_CONFIG.SITES_DOMAIN}`}
                    state={
                      fieldErrors.subdomain || (subdomainResult != null && !subdomainResult.available && subdomain.length >= 3)
                        ? 'error'
                        : subdomainResult?.available && subdomain.length >= 3
                          ? 'success'
                          : 'default'
                    }
                  />
                  {fieldErrors.subdomain ? (
                    <p className="text-sm text-destructive mt-1">{fieldErrors.subdomain}</p>
                  ) : subdomainChecking ? (
                    <p className="text-sm text-muted-foreground mt-1">Checking availability…</p>
                  ) : subdomainResult && subdomain.length >= 3 ? (
                    subdomainResult.available ? (
                      <p className="text-sm text-success mt-1">{subdomainResult.message || 'Available'}</p>
                    ) : (
                      <p className="text-sm text-destructive mt-1">Name is unavailable</p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">
                      You can add a custom domain after you publish this site.
                    </p>
                  )}
                </div>

                <div className={styles.field}>
                  <FaviconField value={favicon} onChange={setFavicon} />
                </div>
              </div>

              <div className={styles.launchFormFooter}>
                <Button type="button" variant="tertiary" onClick={exitWizard}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={onCreate}
                  disabled={creating}
                  className="flex-1"
                >
                  {creating ? 'Creating…' : 'Create site'}
                  <ArrowRight className="h-4 w-4 stroke-[1.5]" />
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default NewSite;
